import { forwardRef, useEffect, useRef, useState } from 'react';
import { produce } from 'immer';

import { useBoundStore } from 'app/store';
import { withNamespace } from 'shared/lib';

import { htmlToDataURL } from '../lib';
import { useCreateSignPreview } from '../model';

import { useModal } from './ModalProvider';

export const SignModal = ({ children }) => {
  return (
    <div className='dimmed-popup-bottom box-wrap sign-modal'>
      <div className='pop-con-wrap'>{children}</div>
    </div>
  );
};

const Title = ({ ptNm }) => {
  return (
    <div className='pop-con-wrap'>
      <div className='pop-con'>
        <p className='pop-tit'>
          {ptNm}님, <br />
          내용을 확인하셨다면 서명해 주세요
        </p>
      </div>
    </div>
  );
};

const SignBox = forwardRef(({ currentSignData, setCurrentSignData, reset }, ref) => {
  const [isDrawing, setIsDrawing] = useState(false);
  const signBoxRef = useRef(null);
  const sign = useBoundStore((state) => state['hsptlzForm'].sign);
  const signCanvasCtx = useBoundStore((state) => state['hsptlzForm'].signCanvasCtx);
  const setSignCanvasCtx = useBoundStore((state) => state['hsptlzForm'].setSignCanvasCtx);
  const signCanvasPlaceHolder = useBoundStore((state) => state['hsptlzForm'].signCanvasPlaceHolder);
  const setSignCanvasPlaceHolder = useBoundStore((state) => state['hsptlzForm'].setSignCanvasPlaceHolder);

  const startDrawing = (e) => {
    e.preventDefault();
    setSignCanvasPlaceHolder(false);
    const touch = e.changedTouches[0]; // 첫 번째 터치
    const { x, y } = ref.current.getBoundingClientRect();
    const startX = touch.clientX - x;
    const startY = touch.clientY - y;
    signCanvasCtx.beginPath(); // 새로운 경로 시작
    signCanvasCtx.moveTo(startX, startY); // 경로 이어서 그리기
    setIsDrawing(true);
  };

  const createSign = () => {
    // 컴포넌트가 마운트된 후 캔버스 설정
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');

    ctx.lineCap = 'round'; // 선 끝 모양
    ctx.lineJoin = 'round'; // 선 이어지는 부분 모양
    ctx.strokeStyle = '#000000'; // 선 색상
    ctx.lineWidth = 5; // 선 두께

    setSignCanvasCtx(ctx);
  };

  const draw = (e) => {
    if (!isDrawing) return; // 그리기 상태가 아니면 무시

    const touch = e.changedTouches[0]; // 첫 번째 터치
    const { x, y } = ref.current.getBoundingClientRect();
    const moveX = touch.clientX - x;
    const moveY = touch.clientY - y;

    signCanvasCtx.lineTo(moveX, moveY); // 경로 이어서 그리기
    signCanvasCtx.stroke(); // 경로 그리기
  };

  const stopDrawing = async (e) => {
    setIsDrawing(false);
    const signFile = await htmlToDataURL(ref.current);
    validateSign(signFile);
  };

  const validateSign = (signFile) => {
    const width = ref.current.width;
    const height = ref.current.height;

    const imageData = signCanvasCtx.getImageData(0, 0, width, height);
    const data = imageData.data;

    let [top, bottom, left, right] = [null, null, null, null];

    const signBox = signBoxRef.current;
    const { width: signBoxWidth, height: signBoxHeight } = signBox.getBoundingClientRect();

    // 픽셀 하나당 4바이트 (RGBA)
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const index = (y * width + x) * 4;
        const alpha = data[index + 3]; // 투명도

        if (alpha !== 0) {
          if (top === null) top = y;
          bottom = y;
          if (left === null || x < left) left = x;
          if (right === null || x > right) right = x;
        }
      }
    }

    if (!(right - left > 30 || bottom - top > 30)) {
      setCurrentSignData({
        signFile: null,
        errorText: '서명이 너무 작아요 좀 더 크게 서명해주세요.',
      });
      return;
    }

    // 10 만큼 여유
    if (left <= 0 || right >= signBoxWidth - 10 || top <= 0 || bottom >= signBoxHeight - 10) {
      setCurrentSignData({
        signFile: null,
        errorText: '영역을 벗어나지 않도록 다시 서명해주세요.',
      });
      return;
    }
    setCurrentSignData({ signFile, errorText: null });
  };

  useEffect(() => {
    const canvas = ref.current;
    const signBox = signBoxRef.current;
    const { width, height } = signBox.getBoundingClientRect();

    canvas.width = width;
    canvas.height = height;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    if (signCanvasCtx) {
      canvas.addEventListener('touchstart', startDrawing, { passive: false });
    }
    return () => {
      canvas.removeEventListener('touchstart', startDrawing);
    };
  }, [signCanvasCtx]);

  useEffect(() => {
    createSign();
  }, [ref.current]);

  return (
    <div className={`sign-box ${currentSignData.errorText ? 'sign-box--error' : ''}`}>
      <div className='sign-gray-box' ref={signBoxRef}>
        <canvas
          ref={ref}
          onFocus={() => {}}
          aria-readonly={true}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
        ></canvas>
        {signCanvasPlaceHolder && !sign.signFile && <span>여기에 서명해주세요</span>}
      </div>
      {currentSignData.errorText && (
        <div className='sign-text'>
          <span>{currentSignData.errorText}</span>
        </div>
      )}
      <div className='sign-clear-btn' onClick={reset}>
        <div className=''>
          <span>모두 지우기</span>
        </div>
      </div>
    </div>
  );
});

SignBox.displayName = 'SignBox';

const Guide = () => {
  return (
    <div className='pop-box'>
      <div className='pop-box-wrap'>
        <div className='box-inner'>
          <div className='box-text'>
            <p>
              전자서명법 제1장 제3조에 따라 공인전자서명 외의 전자 서명은 당사자 간의 약정에 따른 서명, 서명 날인 또는
              기명날인으로서의 효력을 가집니다. 이에 따라 케어챗의 동의 절차는 전자 서명으로 법적 효력을 인정받습니다.
            </p>
            {/* <hr />
            {/* <p> 부모인 경우 추가 (한양대는 해당 경우 없음, 한림대는 해당 경우 있을 것으로 보임)
              형법 제239조에 따라 타인의 서명을 위조하거나 도용하는 행위는 3년 이하의 징역형에 처해질 수 있습니다. 이로
              인해 발생하는 법적 책임은 서명을 입력한 본인에게 있으며, 병원과 케어챗은 책임지지 않습니다.
            </p> */}
          </div>
        </div>
      </div>
    </div>
  );
};

const Button = ({ currentSignData, reset }) => {
  const { signModal, setSignModal } = useModal();
  const sign = useBoundStore((state) => state['hsptlzForm'].sign);
  const setSign = useBoundStore((state) => state['hsptlzForm'].setSign);

  const { createSignPreview } = useCreateSignPreview();

  return (
    <div className='pop-btm'>
      <button
        className='btn btm-btn-secondary'
        onClick={() => {
          reset();
          setSignModal(
            produce(signModal, (draft) => {
              draft.open = false;
            }),
          );
        }}
      >
        <span>취소</span>
      </button>
      <button
        className={currentSignData?.signFile ? 'btn btm-btn-primary' : 'btn btm-btn-secondary'}
        disabled={!currentSignData?.signFile}
        onClick={() => {
          if (!currentSignData.errorText) {
            setSign(
              produce(sign, (draft) => {
                draft[signModal.code] = currentSignData.signFile;
              }),
            );
            createSignPreview(currentSignData.signFile, () => {
              setSignModal(
                produce(signModal, (draft) => {
                  draft.open = false;
                }),
              );
            });
          }
        }}
      >
        <span>서명 완료</span>
      </button>
    </div>
  );
};

export default withNamespace(SignModal, {
  Title,
  SignBox,
  Guide,
  Button,
});
